import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { SetaDireita } from "./ui";

// As peças compartilhadas da Faculdade — o boletim, a Secretaria e a prova.
//
// ---------------------------------------------------------------------------
// TUDO AQUI VIVE SOBRE NAVY
// ---------------------------------------------------------------------------
// A área da Faculdade é a única do hub em que a página inteira é azul-marinho, e
// dentro dela **cor semântica é armadilha**: `text-tinta` clareia no tema escuro
// e vira branco sobre branco, `bg-superficie` fica branco sobre navy. É a
// cicatriz dos cards em escada do login, e ela já voltou pelo caminho inverso no
// HUB DP (título escuro sobre navy, porque a utility `porta` não pinta fundo).
//
// Sobre navy: `text-white` no que é título, `text-navy-ink` no corpo,
// `text-navy-mute` no secundário (#aab4c8 — o cinza da marca reprova em
// contraste sobre #102850), e `laranja-claro` / `sucesso-claro` nos sinais.

/**
 * O selo de estado de um documento ou de uma nota.
 *
 * Três tons, e a escolha NÃO é de gosto: `ok` é o que não pede nada, `espera` é
 * o que está com outra pessoa, `erro` é o que está com o aluno. O aluno lê a cor
 * antes do texto, e é ela que diz se ele precisa fazer algo hoje.
 */
export function Selo({
  tom,
  children,
}: {
  tom: "ok" | "espera" | "erro";
  children: ReactNode;
}) {
  const estilo =
    tom === "ok"
      ? "bg-sucesso-claro/15 text-sucesso-claro"
      : tom === "erro"
        ? "bg-laranja/20 text-laranja-claro"
        : "bg-navy-ink/10 text-navy-mute";
  return (
    <span
      // `data-selo` é escopo de prova: a asserção que mede "o estado apareceu"
      // precisa achar ESTE elemento, e não qualquer span com texto parecido.
      data-selo={tom}
      className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${estilo}`}
    >
      {children}
    </span>
  );
}

/**
 * Pendente · Aprovado · Reprovado — o selo pedido na reunião de 31/08.
 *
 * A situação vem do BANCO (`pos_meu_boletim.situacao`, com `pos_nota_minima()`),
 * e este componente só a veste. Comparar a nota com 6 aqui criaria a segunda
 * definição de "aprovado" — e a primeira é a que decide se o certificado pode
 * ser pedido (`sec_pode_solicitar`, trava 4). Duas contas divergiriam, e a tela
 * diria verde onde a trava diz não.
 *
 * O tom segue a regra do `Selo`: `erro` é o que está com o ALUNO. Reprovado é
 * dele (refaz a prova); pendente também, mas é neutro — quem nunca fez não errou
 * nada. Por isso pendente é `espera`, e não `erro`.
 */
export function SeloDaSituacao({
  situacao,
}: {
  situacao: "pendente" | "aprovado" | "reprovado";
}) {
  const vestido =
    situacao === "aprovado"
      ? { tom: "ok" as const, texto: "Aprovado" }
      : situacao === "reprovado"
        ? { tom: "erro" as const, texto: "Reprovado" }
        : { tom: "espera" as const, texto: "Pendente" };
  return (
    // `data-situacao` carrega o valor CRU, não o texto vestido: a prova compara
    // com o que o banco devolveu, e não com a tradução — se o rótulo mudar de
    // "Aprovado" para "Aprovada", a asserção continua medindo a decisão.
    <span data-situacao={situacao}>
      <Selo tom={vestido.tom}>{vestido.texto}</Selo>
    </span>
  );
}

/**
 * A nota, grande, com o denominador dito.
 *
 * "8" sozinho não informa: pode ser de 10, de 100, de 5. O `/ 10` fica ao lado
 * em corpo menor — é a escala, não o número. E `—` quando não há nota: um zero
 * ali seria mentira sobre quem não fez a prova.
 */
export function Nota({ valor }: { valor: number | null }) {
  if (valor === null || valor === undefined) {
    return <span className="font-display text-2xl font-semibold text-navy-mute">—</span>;
  }
  return (
    <span className="font-display text-2xl font-bold tabular-nums text-white">
      {valor.toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 1 })}
      <span className="ml-0.5 text-sm font-semibold text-navy-mute">/ 10</span>
    </span>
  );
}

/**
 * Um aviso dentro da área navy.
 *
 * Existe porque `Aviso` de `ui.tsx` é de fundo claro — usá-lo aqui daria uma
 * caixa branca no meio do azul-marinho.
 */
export function AvisoNavy({
  titulo,
  children,
  tom = "espera",
  acao,
}: {
  titulo: string;
  children?: ReactNode;
  tom?: "espera" | "erro";
  acao?: ReactNode;
}) {
  return (
    <div
      className={`tv-card flex flex-wrap items-start justify-between gap-4 p-5 ${
        tom === "erro" ? "border-laranja/40" : ""
      }`}
    >
      <div className="min-w-56 flex-1">
        <h3 className="font-display font-semibold text-white">{titulo}</h3>
        {children && <p className="mt-1 text-sm text-navy-mute">{children}</p>}
      </div>
      {acao}
    </div>
  );
}

/** O cabeçalho de uma seção dentro da área navy. */
export function TituloDaSecao({
  children,
  nota,
  className = "",
}: {
  children: ReactNode;
  nota?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`mb-4 flex flex-wrap items-baseline justify-between gap-2 ${className}`}>
      <h2 className="text-lg font-bold text-white">{children}</h2>
      {nota && <p className="text-sm text-navy-mute">{nota}</p>}
    </div>
  );
}

/** Data curta, sem hora. `null` vira `—`, e nunca a data de hoje por engano. */
export function data(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/**
 * Uma data PURA (`date` do Postgres, sem hora) em pt-BR.
 *
 * `data()` acima serve para `timestamptz`, que carrega o instante e o fuso. Um
 * `date` é outra coisa: `new Date("2018-12-15")` é interpretado pelo JS como
 * **meia-noite UTC**, e em São Paulo (-03:00) isso é 21h do dia **14** — então
 * a data sai um dia ANTES, sempre.
 *
 * Não é hipótese: foi medido em 22/09 pela prova de tela da colação de grau, no
 * primeiro caso. Gravado 15/12/2018, a tela dizia 14/12/2018; gravado
 * 01/06/2019, dizia 31/05/2019 — e o erro atravessa o mês e o ano. Numa data
 * que a secretaria usa para decidir requisito de pós-graduação, um dia a menos
 * é um fato errado com a cara de certo.
 *
 * A saída é não deixar `Date` entrar na conta: a string já vem no formato
 * `AAAA-MM-DD` do Postgres, e o que ela precisa é ser lida, não convertida. É a
 * mesma família da decisão do fato gerador ao MEIO-DIA em `montarCalculo.ts`,
 * pelo caminho que dispensa o relógio.
 */
export function dataDoDia(iso: string | null | undefined): string {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "—";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** O botão-pílula laranja da casa, como `<Link>`. */
export function AcaoPrincipal({
  para,
  children,
  className = "",
}: {
  para: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <Link to={para} className={`pill-cta shrink-0 ${className}`}>
      {children} <SetaDireita />
    </Link>
  );
}
